/**
 * SwatchApply
 * -----------------------------------------------------------------------------
 * Shared helper for applying a swatch color to the current dropcolor slot.
 * Panels can prep their swatch payload (cache hydration, metadata injection),
 * then call this helper to ensure the exact same CanvasState workflow is used.
 */
'use strict'

import Color from '../models/Color'
import CXImageAction from '../ImagePaintProcessing/CXImageAction'
import SliderUIController from '../controllers/SliderUIController'
import { updateTextureSwatchBackgroundColor } from './visualizer-texture-swatch-panel.js'

/**
 * Named modes used by applySwatchColorToCurrentSlot.
 * Keeping them centralized avoids magic strings at call sites.
 */
export const SWATCH_APPLY_MODE = Object.freeze({
    SINGLE: 'single',
    BATCH: 'batch',
    DRAG_COLOR: 'drag-color',
    DRAG_TEXTURE: 'drag-texture',
})

/**
 * Apply a swatch color to the active dropcolor slot and sync UI/state.
 *
 * This is the single pipeline used by multiple panels (swatches, recents,
 * closest colors, harmonies, drag/drop). Instead of passing many flags from
 * each call site, the function accepts a small `mode` describing the intent.
 *
 * Modes (and who uses them):
 * - `single`
 *   - Default for click-based panels (closest colors, recents, swatches).
 *   - Full UI sync: history action, repaint, slider sync, highlight.
 * - `batch`
 *   - HarmonyManager applies several colors in a loop.
 *   - No per-color history, no repaint until the batch is done, no highlight.
 * - `drag-color`
 *   - DropHandler for color drag/drop.
 *   - Repaint, but skip slider sync + highlight (DropHandler does its own).
 * - `drag-texture`
 *   - DropHandler for texture / texture-swatch drag/drop.
 *   - Refresh texture canvas only, skip slider sync + highlight.
 *
 * Why no separate "drag-color + drag-texture-swatch" mode?
 * Both texture drop types share the same behavior; the only difference is the
 * optional `textureID`, so a single `drag-texture` preset is enough.
 *
 * @param {Object} options
 * @param {Object} options.app - Visualizer application instance.
 * @param {string} [options.mode=SWATCH_APPLY_MODE.SINGLE]
 *        Preset that controls history, repaint, highlight, and slider sync.
 * @param {Object} [options.swatchColor]
 *        SwatchColor payload to apply (used when `color` is not provided).
 * @param {Color|null} [options.color=null]
 *        Pre-built Color model (skips Color.create).
 * @param {(swatchColor:Object, app:Object) => Object} [options.prepareSwatchColor]
 *        Optional pre-processor to hydrate/normalize the swatch payload before Color.create.
 * @param {(color:Color, app:Object) => boolean} [options.canHighlight]
 *        Optional guard before highlighting the source swatch in the UI.
 * @param {string|null} [options.backgroundImageURL=null]
 *        Texture image URL when the swatch represents a texture/texture-swatch.
 * @param {number|string|null} [options.textureID=null]
 *        Texture tile id for texture-swatch colors (used by CanvasState).
 * @returns {Promise<Color>} Resolved Color model created by Color.create.
 */
export async function applySwatchColorToCurrentSlot({
    app,
    mode = SWATCH_APPLY_MODE.SINGLE,
    swatchColor,
    color = null,
    prepareSwatchColor = (color) => color,
    canHighlight = null,
    backgroundImageURL = null,
    textureID = null,
}) {
    const MODE_PRESETS = {
        [SWATCH_APPLY_MODE.SINGLE]: {
            rebuildMode: 'paint',
            rebuildWhenEmpty: true,
            syncSliders: true,
            highlight: true,
            updateInfos: true,
            withAction: true,
        },
        [SWATCH_APPLY_MODE.BATCH]: {
            rebuildMode: 'none',
            rebuildWhenEmpty: false,
            syncSliders: false,
            highlight: false,
            updateInfos: false,
            withAction: false,
        },
        [SWATCH_APPLY_MODE.DRAG_COLOR]: {
            rebuildMode: 'paint',
            rebuildWhenEmpty: false,
            syncSliders: false,
            highlight: false,
            updateInfos: true,
            withAction: true,
        },
        [SWATCH_APPLY_MODE.DRAG_TEXTURE]: {
            rebuildMode: 'texture',
            rebuildWhenEmpty: true,
            syncSliders: false,
            highlight: false,
            updateInfos: true,
            withAction: true,
        },
    }

    const preset = MODE_PRESETS[mode] || MODE_PRESETS.single
    const {
        rebuildMode,
        rebuildWhenEmpty,
        syncSliders,
        highlight,
        updateInfos,
        withAction,
    } = preset

    const canvasState = app.canvasState
    const colorSample = canvasState.colorSample

    const slot = colorSample.workingColorPos || 1
    colorSample.workingColorPos = slot

    const action = withAction ? new CXImageAction(CXImageAction.ActionType.ActionTypeChangeColorSample) : null
    if (action) action.initAsColorSampleChangeAction(colorSample)

    const oldColor = colorSample.getColorByNumber(slot)
    const newColor =
        color ||
        (await Color.create({
            swatch_color_data: prepareSwatchColor(swatchColor, app),
            dropcolor_position: slot,
            current_swatch_id: app.swatchSelection.currentSwatchId,
            listOfColorChartsServerID: app.listOfColorChartsServerID,
        }))

    await canvasState.setCurrentColorFromChart(newColor, oldColor, backgroundImageURL, textureID, app.swatchSelection)

    // Keep texture-swatch previews in sync with the newly applied color.
    const newSwatchColor = newColor?.swatch_color
    if (newSwatchColor?.id && app?.swatchSelection?.currentTextureSwatch) {
        updateTextureSwatchBackgroundColor({
            r: newSwatchColor.sr,
            g: newSwatchColor.sg,
            b: newSwatchColor.sb,
            swatch_color_id: newSwatchColor.id,
        })
    }

    // Optional: update UI/infos once (can be disabled for batch operations).
    if (updateInfos) {
        colorSample.updateColorInfos()
    }
    if (action) {
        action.setNewColorSampleForColorSampleChangeAction(colorSample)
        canvasState.addAction(action)
    }
    if (rebuildMode === 'paint') {
        if (rebuildWhenEmpty || oldColor) {
            canvasState.rebuildPaintedImage()
        }
    } else if (rebuildMode === 'texture') {
        canvasState.refreshTextureCanvas()
    }
    if (syncSliders) {
        SliderUIController.syncToColor(colorSample.getCurrentColor())
    }

    if (highlight && app.paintingListener?.dropHandler?._highlightSourceSwatch) {
        if (!canHighlight || canHighlight(newColor, app)) {
            await app.paintingListener.dropHandler._highlightSourceSwatch(newColor)
        }
    }

    return newColor
}
